import { useEffect, useRef, useState } from 'react'
import { getRacingTicket, dropRacingTicket } from '../services/racingTicket'

const WS_URL = 'wss://api.rapidoddsapi.com/racing/ws'
const RECONNECT_MS = 5000
// A tab hidden this long closes its socket (phone in a pocket, tab left open
// all day). Shorter blips, like alt-tabbing to a bookmaker, keep it open.
const HIDDEN_CLOSE_MS = 60 * 1000

// Live racing odds straight from the racing API. Each connect first gets a
// ticket from the racing-ticket edge function (checks login + plan), so
// the oa_ key never reaches the browser. On every (re)connect, and every time
// `subscriptions` changes, the server sends a fresh `subscribed` snapshot per
// subscription, so treat it as a full replace.
//
// subscriptions: subscribe bodies, e.g. [{ code: 'thoroughbred', tracks: '*' }].
//   Changing them swaps what the open socket follows, no reconnect.
// onMessage: called with every parsed message (subscribed, race_update, ...)
// Returns status: idle | connecting | connected | reconnecting | no_access | too_many_tabs
export default function useRacingFeed({ enabled, subscriptions, onMessage }) {
  const [status, setStatus] = useState('idle')
  const [hiddenLong, setHiddenLong] = useState(false)
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage
  const subscriptionsRef = useRef(subscriptions)
  subscriptionsRef.current = subscriptions
  const wsRef = useRef(null)
  const subscriptionsKey = JSON.stringify(subscriptions)

  useEffect(() => {
    let timer = null
    const onVisibilityChange = () => {
      clearTimeout(timer)
      if (document.hidden) timer = setTimeout(() => setHiddenLong(true), HIDDEN_CLOSE_MS)
      else setHiddenLong(false)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  const active = enabled && !hiddenLong

  useEffect(() => {
    if (!active) {
      setStatus('idle')
      return
    }
    let ws = null
    let timer = null
    let stopped = false

    const retry = () => {
      if (stopped) return
      setStatus('reconnecting')
      timer = setTimeout(connect, RECONNECT_MS)
    }

    const connect = async () => {
      setStatus((s) => (s === 'reconnecting' ? s : 'connecting'))
      const { ticket, noAccess } = await getRacingTicket()
      if (stopped) return
      if (!ticket) {
        // 403 = plan has no racing, retrying won't change that
        if (noAccess) setStatus('no_access')
        else retry()
        return
      }

      let opened = false
      ws = new WebSocket(`${WS_URL}?ticket=${encodeURIComponent(ticket)}`)
      wsRef.current = ws
      ws.onopen = () => {
        opened = true
        setStatus('connected')
        for (const sub of subscriptionsRef.current) ws.send(JSON.stringify({ action: 'subscribe', ...sub }))
      }
      ws.onmessage = (e) => {
        let msg
        try { msg = JSON.parse(e.data) } catch { return }
        onMessageRef.current(msg)
      }
      ws.onclose = (e) => {
        if (wsRef.current === ws) wsRef.current = null
        // Turned away at connect: don't reuse that ticket on the retry.
        if (!opened) dropRacingTicket(ticket)
        if (stopped) return
        // 4029 = this user already has 2 tabs connected
        if (e.code === 4029) setStatus('too_many_tabs')
        else retry()
      }
    }

    connect()
    return () => {
      stopped = true
      clearTimeout(timer)
      ws?.close()
    }
  }, [active])

  // Swap subscriptions on the open socket. Before it opens, onopen sends the
  // latest ones anyway.
  useEffect(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ action: 'unsubscribe', all: true }))
    for (const sub of subscriptionsRef.current) ws.send(JSON.stringify({ action: 'subscribe', ...sub }))
  }, [subscriptionsKey])

  return status
}
